import React from 'react';

import {Button, Icon, IconButton, Text} from '@albert-io/atomic';
import type {IconProp, IconStyle} from '@albert-io/atomic/atoms/Icon/Icon.react';

import {history} from 'client/history';
import {ClassworkRow} from 'client/TeacherDashboard/components/ClassworkRow';
import {ErrorRetryState} from 'client/TeacherDashboard/components/ErrorRetryState';
import {
  SkillPathListGroup,
  SkillPathListGroupEmpty
} from 'client/TeacherDashboard/SkillPaths/SkillPathListGroup';
import {formatDateRange, formatDateTime, getDueCopy} from 'client/TeacherDashboard/rowDateCopy';
import {AdaptiveOnboardingEmptyState} from 'client/TeacherDashboard/components/AdaptiveEmptyState';
import {STATUS_SEGMENTS} from 'client/TeacherDashboard/SkillPaths/constants';
import type {SkillPathStatusSegment} from 'client/TeacherDashboard/SkillPaths/types';
import {DiagnosticStatus} from 'gql/types';

import type {DiagnosticListFieldsFragment} from './queries/Diagnostics.queries.generated';

const STATUS_SEGMENT: Record<DiagnosticStatus, SkillPathStatusSegment> = {
  [DiagnosticStatus.Open]: 'open',
  [DiagnosticStatus.Closed]: 'closed',
  [DiagnosticStatus.Scheduled]: 'scheduled'
};

const STATUS_DISPLAY: Record<
  SkillPathStatusSegment,
  {color: 'tertiary' | 'warning'; icon?: IconProp; iconStyle?: IconStyle}
> = {
  open: {color: 'tertiary'},
  scheduled: {color: 'warning', icon: 'clock', iconStyle: 'light'},
  closed: {color: 'tertiary', icon: 'ban', iconStyle: 'regular'}
};

const completionCopy = ({
  countOfCompletedStudents,
  countOfIncompleteStudents
}: DiagnosticListFieldsFragment): string[] => {
  const total = countOfCompletedStudents + countOfIncompleteStudents;
  return total > 0 ? [`${countOfCompletedStudents} / ${total} completed`] : [];
};

const getSubtitleSections = (
  diagnostic: DiagnosticListFieldsFragment,
  status: SkillPathStatusSegment,
  showExactDueDate: boolean
): string[] => {
  if (status === 'scheduled') {
    return [
      diagnostic.minStartDate
        ? `Starts ${formatDateRange(diagnostic.minStartDate, diagnostic.maxStartDate, formatDateTime)}`
        : 'Scheduled',
      ...completionCopy(diagnostic)
    ];
  }
  const dueCopy = getDueCopy(
    {minDueDate: diagnostic.minDueDate, maxDueDate: diagnostic.maxDueDate},
    showExactDueDate
  );
  if (status === 'closed') {
    return [...(dueCopy ? ['Closed', dueCopy] : ['Closed']), ...completionCopy(diagnostic)];
  }
  return [dueCopy || 'Open', ...completionCopy(diagnostic)];
};

const editPath = (diagnostic: DiagnosticListFieldsFragment): string =>
  `/diagnostics/${diagnostic.diagnosticTemplateId}/assign/${diagnostic.id}`;

const DiagnosticRow = ({
  diagnostic,
  segment
}: {
  diagnostic: DiagnosticListFieldsFragment;
  segment: SkillPathStatusSegment;
}) => {
  // The list is filtered to one status, so the server roll-up always matches the segment; the
  // fallback only covers a row arriving without one.
  // Falls back rather than indexes blind: a status this bundle predates would otherwise throw here
  // and take the whole tab down.
  const status = (diagnostic.status && STATUS_SEGMENT[diagnostic.status]) ?? segment;
  const {color, icon, iconStyle} = STATUS_DISPLAY[status];
  const goToEdit = () => history.push(editPath(diagnostic));

  return (
    <ClassworkRow
      actions={
        <>
          <IconButton
            disabled
            icon='chart-line'
            iconStyle='regular'
            label='View Report'
            tooltip='View Report'
            size='s'
          />
          <IconButton
            icon='pencil'
            iconStyle='regular'
            label='Edit Diagnostic'
            tooltip='Edit Diagnostic'
            size='s'
            onClick={goToEdit}
          />
        </>
      }
      data-testid='diagnostic-row'
      icon={['fal', 'file-contract']}
      iconStyle='light'
      renderSubtitle={(isHovered) => (
        <Text as='div' className='truncate' color={color} size='s'>
          {icon ? (
            <Icon className='mr-x0-half text-xs!' color={color} icon={icon} iconStyle={iconStyle} />
          ) : null}
          {getSubtitleSections(diagnostic, status, isHovered).map((part, index) => (
            <React.Fragment key={part}>
              {index > 0 && <span className='mx-x1'>•</span>}
              {part}
            </React.Fragment>
          ))}
        </Text>
      )}
      title={
        <Text className='content-primary truncate' size='m'>
          {diagnostic.name}
        </Text>
      }
      onOpen={goToEdit}
    />
  );
};

interface Props {
  diagnostics: DiagnosticListFieldsFragment[];
  /** Total matching the status + filters, shown beside the group title. */
  total: number;
  segment: SkillPathStatusSegment;
  /** Every status is empty under the current filters — show the pitch, not "no open …". */
  isEmptyInEveryStatus: boolean;
  /** More rows match than are loaded; the button both fetches and reveals them. */
  hasMore: boolean;
  isLoadingMore: boolean;
  onShowMore: () => void;
  classroomId?: string;
  hasError: boolean;
  onRetry: () => void;
}

export const DiagnosticsGroup = ({
  diagnostics,
  total,
  segment,
  isEmptyInEveryStatus,
  hasMore,
  isLoadingMore,
  onShowMore,
  classroomId,
  hasError,
  onRetry
}: Props) => {
  const statusLabel = STATUS_SEGMENTS.find((s) => s.value === segment)?.label.toLowerCase() ?? '';
  const remaining = total - diagnostics.length;

  return (
    <SkillPathListGroup count={hasError ? undefined : total} title='Diagnostics'>
      {hasError ? (
        <ErrorRetryState copy='We couldn’t load your diagnostics.' onRetry={onRetry} />
      ) : diagnostics.length === 0 ? (
        <SkillPathListGroupEmpty inset={isEmptyInEveryStatus ? 'panel' : 'text'}>
          {isEmptyInEveryStatus ? (
            <AdaptiveOnboardingEmptyState
              classroomId={classroomId}
              layout='inline'
              variant='diagnostics'
            />
          ) : (
            <Text color='secondary'>No {statusLabel} diagnostics</Text>
          )}
        </SkillPathListGroupEmpty>
      ) : (
        <>
          <ul className='m-x0 p-x0 flex list-none flex-col'>
            {diagnostics.map((diagnostic) => (
              <li key={diagnostic.id}>
                <DiagnosticRow diagnostic={diagnostic} segment={segment} />
              </li>
            ))}
          </ul>
          {hasMore && (
            <div className='pl-x8'>
              <Button
                color='secondary'
                disabled={isLoadingMore}
                roundness='pill'
                size='s'
                variant='outlined'
                onClick={onShowMore}
              >
                {isLoadingMore ? 'Loading…' : `Show ${remaining} More`}
              </Button>
            </div>
          )}
        </>
      )}
    </SkillPathListGroup>
  );
};
